import { supabase } from "../lib/supabase";

const API_BASE =
  (process.env.REACT_APP_AI_API_URL || process.env.REACT_APP_AI_API_BASE_URL || "https://colobongo.onrender.com").replace(/\/$/, "");
const DEVICE_ID_KEY = "colobongo_scan_device_id";
const LOCAL_INSPIRATION_HISTORY_KEY = "colobongo_studio_inspiration_history_v1";
const LOCAL_INSPIRATION_LIMIT = 2;

export const INSPIRATION_SCAN_COST = 2;
export const STUDIO_INSPIRATION_ENDPOINT = `${API_BASE}/api/ai/inspiration/generate`;
export const STUDIO_INSPIRATION_RECENT_ENDPOINT = `${API_BASE}/api/ai/inspiration/recent`;
export const INSPIRATION_HISTORY_UPDATED_EVENT = "colobongo:inspiration-history-updated";
const INSPIRATION_REQUEST_TIMEOUT_MS = 180000;

function getOrCreateScanDeviceId() {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;

  const generated = `scan-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(DEVICE_ID_KEY, generated);
  return generated;
}

async function getAccessToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch (_error) {
    return null;
  }
}

function normalizeQuotaPayload(payload = {}) {
  return {
    quotaOk: Boolean(payload?.quota_ok),
    quotaExceeded: Boolean(payload?.quota_exceeded),
    scansRemaining: Number(payload?.scans_remaining ?? 0),
    scansUsedToday: Number(payload?.scans_used_today ?? 0),
    dailyLimit: Number(payload?.daily_limit ?? 0),
    isSupporter: Boolean(payload?.is_supporter),
  };
}

function normalizeRecentItem(item = {}) {
  return {
    id: String(item?.id || ""),
    imageUrl: String(item?.image_url || item?.imageUrl || "").trim(),
    mimeType: String(item?.mime_type || item?.mimeType || "image/png"),
    createdAt: item?.created_at || item?.createdAt || null,
    challengeCategory: String(item?.challenge_category || item?.challengeCategory || "").trim() || null,
    lang: item?.lang === "en" ? "en" : "fr",
  };
}

function sortByNewest(items = []) {
  return [...items].sort((a, b) => {
    const aTs = Date.parse(a?.createdAt || "") || 0;
    const bTs = Date.parse(b?.createdAt || "") || 0;
    return bTs - aTs;
  });
}

function readLocalHistoryStore() {
  if (typeof window === "undefined" || !window.localStorage) {
    return { byUser: {} };
  }

  try {
    const raw = window.localStorage.getItem(LOCAL_INSPIRATION_HISTORY_KEY);
    if (!raw) return { byUser: {} };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { byUser: {} };
    return {
      byUser: parsed.byUser && typeof parsed.byUser === "object"
        ? parsed.byUser
        : {},
    };
  } catch (_error) {
    return { byUser: {} };
  }
}

function writeLocalHistoryStore(store) {
  if (typeof window === "undefined" || !window.localStorage) return false;
  window.localStorage.setItem(LOCAL_INSPIRATION_HISTORY_KEY, JSON.stringify(store));
  window.dispatchEvent(new CustomEvent(INSPIRATION_HISTORY_UPDATED_EVENT));
  return true;
}

export function getLocalStudioInspirations({ userId, limit = LOCAL_INSPIRATION_LIMIT } = {}) {
  if (!userId) return [];
  const normalizedLimit = Math.max(1, Math.floor(Number(limit) || LOCAL_INSPIRATION_LIMIT));
  const store = readLocalHistoryStore();
  const rows = Array.isArray(store?.byUser?.[userId]) ? store.byUser[userId] : [];
  return sortByNewest(rows
    .map(normalizeRecentItem)
    .filter((item) => item.id && item.imageUrl)).slice(0, normalizedLimit);
}

export function saveLocalStudioInspiration(
  {
    userId,
    imageUrl,
    mimeType = "image/png",
    language = "fr",
    challengeCategory = null,
  } = {}
) {
  if (!userId || !imageUrl) return [];

  const store = readLocalHistoryStore();
  const previousRows = Array.isArray(store?.byUser?.[userId]) ? store.byUser[userId] : [];
  const normalizedRows = sortByNewest(previousRows
    .map(normalizeRecentItem)
    .filter((item) => item.id && item.imageUrl));

  const nextItem = {
    id: `inspiration-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    imageUrl: String(imageUrl),
    mimeType: String(mimeType || "image/png"),
    createdAt: new Date().toISOString(),
    challengeCategory: challengeCategory ? String(challengeCategory) : null,
    lang: language === "en" ? "en" : "fr",
  };

  const nextRows = [nextItem, ...normalizedRows.filter((item) => item.imageUrl !== nextItem.imageUrl)]
    .slice(0, LOCAL_INSPIRATION_LIMIT);

  const nextStore = {
    ...store,
    byUser: {
      ...(store.byUser || {}),
      [userId]: nextRows,
    }
  };

  try {
    writeLocalHistoryStore(nextStore);
    return nextRows;
  } catch (_error) {
    const fallbackRows = nextRows.slice(0, 1);
    try {
      writeLocalHistoryStore({
        ...store,
        byUser: {
          ...(store.byUser || {}),
          [userId]: fallbackRows,
        }
      });
    } catch (_fallbackError) {
      return [];
    }
    return fallbackRows;
  }
}

/**
 * CODEX_PROMPT_90 : historique servi par l'API.
 *
 * GET /api/ai/inspiration/recent existe depuis PROMPT_89 mais n'etait jamais
 * appele : l'historique vivait uniquement en localStorage, donc il disparaissait
 * au changement d'appareil ou au nettoyage du navigateur.
 *
 * Les URL renvoyees sont signees et valables 30 minutes
 * (INSPIRATION_SIGNED_URL_TTL_SECONDS) : une vignette peut expirer pendant que
 * la page est ouverte, l'appelant doit prevoir un rechargement.
 *
 * @returns {Promise<Array>} items normalises (camelCase), tries du plus recent
 */
export async function fetchRecentStudioInspirations() {
  const token = await getAccessToken();
  const headers = {
    "X-Scan-Device-Id": getOrCreateScanDeviceId(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(STUDIO_INSPIRATION_RECENT_ENDPOINT, {
    method: "GET",
    headers,
  });
  if (!response.ok) {
    const error = new Error("Could not load recent inspirations");
    error.status = response.status;
    throw error;
  }

  const payload = await response.json().catch(() => ({}));
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return sortByNewest(
    items.map(normalizeRecentItem).filter((item) => item.id && item.imageUrl)
  );
}

export async function generateStudioInspiration(
  file,
  {
    language = "fr",
    challengeCategory = "",
    selectedBrands = [],
    lightEffect = "",
    challengeMode = false,
    paletteHex = [],
  } = {}
) {
  if (!file) {
    throw new Error("No file provided for Inspiration AI");
  }

  const token = await getAccessToken();
  const headers = {
    "X-Scan-Device-Id": getOrCreateScanDeviceId()
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const formData = new FormData();
  formData.append("image", file);
  formData.append("lang", language === "en" ? "en" : "fr");
  if (challengeCategory) {
    formData.append("challenge_category", challengeCategory);
  }
  if (selectedBrands && selectedBrands.length > 0) {
    formData.append("selected_brands", JSON.stringify(selectedBrands));
  }
  // Options Inspiration V2 (CODEX_PROMPT_89) — champs absents quand inactifs
  // pour garder la requete identique a l'existant (retro-compat backend).
  if (lightEffect) {
    formData.append("light_effect", String(lightEffect));
  }
  if (challengeMode) {
    formData.append("challenge_mode", "1");
  }
  if (Array.isArray(paletteHex) && paletteHex.length > 0) {
    formData.append("palette_hex", JSON.stringify(paletteHex));
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), INSPIRATION_REQUEST_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(STUDIO_INSPIRATION_ENDPOINT, {
      method: "POST",
      headers,
      body: formData,
      signal: controller.signal
    });
  } catch (fetchError) {
    clearTimeout(timeoutId);
    const isTimeout = fetchError?.name === "AbortError";
    const friendly = isTimeout
      ? (language === "fr"
        ? "Oups, l'inspiration est timide aujourd'hui… Réessaie dans un instant 🎨"
        : "Oh, inspiration is a bit shy today… Please try again in a moment 🎨")
      : (language === "fr"
        ? "On n'a pas pu joindre le serveur. Vérifie ta connexion et réessaie."
        : "We couldn't reach the server. Check your connection and try again.");
    const wrapped = new Error(friendly);
    wrapped.cause = fetchError;
    wrapped.isTimeout = isTimeout;
    throw wrapped;
  } finally {
    clearTimeout(timeoutId);
  }

  const payload = await response.json().catch(() => ({}));
  const detail = payload?.detail || payload || {};

  if (!response.ok) {
    const error = new Error(
      detail?.message ||
      (language === "fr"
        ? "Impossible de générer une inspiration pour le moment."
        : "Could not generate an inspiration right now.")
    );
    error.status = response.status;
    error.quotaExceeded = Boolean(detail?.quota_exceeded) || response.status === 429;
    error.quota = normalizeQuotaPayload(detail);
    error.debug = {
      errorCode: detail?.error_code || null,
      providerStatus: detail?.provider_status ?? null,
      providerRequestId: detail?.provider_request_id || null,
      clientRequestId: detail?.client_request_id || null,
    };
    throw error;
  }

  if (!payload?.image_base64) {
    throw new Error(
      language === "fr"
        ? "Le serveur n'a pas renvoye d'image."
        : "The server did not return an image."
    );
  }

  const mimeType = payload?.mime_type || "image/png";
  const previewUrl = `data:${mimeType};base64,${payload.image_base64}`;

  return {
    type: "image",
    payload: {
      previewUrl,
      mimeType,
      size: Math.max(0, Math.floor((payload.image_base64?.length || 0) * 0.75))
    },
    scanCostCharged: Number(payload?.scan_cost_charged ?? INSPIRATION_SCAN_COST),
    quota: normalizeQuotaPayload(payload)
  };
}
